import { useCallback, useEffect, useMemo, useState } from 'react';
import { get, postWithAuth } from '../../../utils/apiClient';

type HistoryItem = {
  id: string;
  display_name?: string | null;
  generic_drug_name?: string | null;
  payer?: string | null;
  effective_date?: string | null;
  last_fill_date?: string | null;
  days_supply?: number | null;
  quantity?: string | null;
  directions?: string | null;
  strength?: string | null;
  route?: string | null;
  otc?: boolean | null;
  disposition: string;
  reviewed: boolean;
  dispositioned_at?: string | null;
};

type ChartMedication = {
  id: string;
  medication_name?: string | null;
  custom_medication_name?: string | null;
  dosage?: string | null;
  frequency?: string | null;
  status?: string | null;
  end_date?: string | null;
};

interface MedicationHistoryReviewProps {
  patientId: string;
  onMedicationsRefreshed?: () => Promise<void> | void;
}

const dispositionLabels = {
  add_confirm_current: 'Add / confirm as current',
  already_represented: 'Already represented in chart',
  status_unknown: 'Client unsure / status unknown',
  historical_not_current: 'Historical / not current',
  ignore_not_relevant: 'Ignore / not clinically relevant',
} as const;

type Disposition = keyof typeof dispositionLabels;

const MARK_AS_OPTIONS: { value: Disposition; label: string }[] = [
  { value: 'add_confirm_current', label: 'Currently taking — add to chart' },
  { value: 'already_represented', label: 'Already in chart' },
  { value: 'historical_not_current', label: 'Not current — historical' },
  { value: 'ignore_not_relevant', label: 'Not relevant — ignore' },
  { value: 'status_unknown', label: 'Unsure — leave for review' },
];

function medicationLabel(item: HistoryItem) {
  return item.display_name || item.generic_drug_name || 'Unknown medication';
}

function observationDetail(item: HistoryItem) {
  const parts = [
    `Payer: ${item.payer || 'Not provided'}`,
    `Last fill: ${item.last_fill_date || 'Not provided'}`,
    item.effective_date ? `Effective: ${item.effective_date}` : null,
    item.days_supply != null ? `Days supply: ${item.days_supply}` : null,
    item.quantity ? `Quantity: ${item.quantity}` : null,
    item.strength ? `Strength: ${item.strength}` : null,
    item.directions ? `Directions: ${item.directions}` : null,
    item.route ? `Route: ${item.route}` : null,
  ].filter(Boolean);
  return parts.join(' · ');
}

function chartMedicationOptionLabel(med: ChartMedication) {
  const name = med.medication_name || med.custom_medication_name || 'Medication';
  const details = [med.dosage, med.frequency].filter(Boolean).join(' ');
  const inactive = med.status && med.status !== 'active';
  return `${name}${details ? ` — ${details}` : ''}${inactive ? ' (historical)' : ''}`;
}

export default function MedicationHistoryReview({
  patientId,
  onMedicationsRefreshed,
}: MedicationHistoryReviewProps) {
  const [items, setItems] = useState<HistoryItem[]>([]);
  const [chartMedications, setChartMedications] = useState<ChartMedication[]>([]);
  const [selectedAction, setSelectedAction] = useState<Record<string, Disposition | ''>>({});
  const [selectedChartMedications, setSelectedChartMedications] = useState<Record<string, string>>({});
  const [updateFromHistory, setUpdateFromHistory] = useState<Record<string, boolean>>({});
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(false);
  const [reconcilingId, setReconcilingId] = useState<string | null>(null);
  const [message, setMessage] = useState('');
  const [expanded, setExpanded] = useState(false);
  const [showReviewed, setShowReviewed] = useState(false);
  const [editingReviewed, setEditingReviewed] = useState<Record<string, boolean>>({});

  const loadHistory = useCallback(async () => {
    setLoading(true);
    try {
      const [historyResponse, medicationResponse] = await Promise.all([
        get(`/erx/medication-history/${patientId}?show_reviewed=${showReviewed}`),
        get(`/medications/patients/${patientId}/medications`),
      ]);
      if (historyResponse.ok) {
        const data = await historyResponse.json();
        setItems(data.history || []);
      }
      if (medicationResponse.ok) {
        const data = await medicationResponse.json();
        setChartMedications(data.medications || []);
      }
    } finally {
      setLoading(false);
    }
  }, [patientId, showReviewed]);

  useEffect(() => {
    if (expanded) void loadHistory();
  }, [expanded, loadHistory]);

  // Collapse repeat payer observations of the same reported medication into the
  // most recent fill; earlier fills stay fully visible under an expander. Only
  // the reported name groups them — route, formulation, directions, dates and
  // prescriber may differ per observation and are preserved per row.
  const groups = useMemo(() => {
    const byName = new Map<string, HistoryItem[]>();
    items.forEach((item) => {
      const key = medicationLabel(item).trim().toLowerCase();
      const list = byName.get(key) || [];
      list.push(item);
      byName.set(key, list);
    });
    return [...byName.values()].map((list) => {
      const sorted = [...list].sort((a, b) =>
        (b.last_fill_date || '').localeCompare(a.last_fill_date || ''),
      );
      return { latest: sorted[0], earlier: sorted.slice(1) };
    });
  }, [items]);

  const syncHistory = async () => {
    setLoading(true);
    setMessage('');
    try {
      const response = await postWithAuth(`/erx/medication-history/${patientId}/sync`, {});
      if (!response.ok) throw new Error('Unable to import insurance medication history');
      await loadHistory();
      setMessage('Insurance medication history imported for review.');
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Unable to import medication history');
    } finally {
      setLoading(false);
    }
  };

  const reconcile = async (item: HistoryItem, disposition: Disposition) => {
    setReconcilingId(item.id);
    setMessage('');
    try {
      const response = await postWithAuth(`/erx/medication-history/${item.id}/disposition`, {
        disposition,
        patient_medication_id: disposition === 'already_represented'
          ? selectedChartMedications[item.id]
          : undefined,
        update_from_history:
          disposition === 'already_represented' ? Boolean(updateFromHistory[item.id]) : undefined,
      });
      if (!response.ok) {
        setMessage('Unable to save this reconciliation decision.');
        return;
      }
      await loadHistory();
      const updatesCanonicalMedication = (
        disposition === 'add_confirm_current'
        || disposition === 'status_unknown'
        || disposition === 'historical_not_current'
        || (disposition === 'already_represented' && Boolean(updateFromHistory[item.id]))
      );
      if (updatesCanonicalMedication) {
        await onMedicationsRefreshed?.();
      }
      setEditingReviewed((current) => {
        const next = { ...current };
        delete next[item.id];
        return next;
      });
      setMessage('Reconciliation decision saved.');
    } catch {
      setMessage('Unable to save this reconciliation decision.');
    } finally {
      setReconcilingId(null);
    }
  };

  const renderActionControls = (item: HistoryItem, labelSuffix = '') => {
    const isReconciling = reconcilingId === item.id;
    const action = selectedAction[item.id] || '';
    const needsChartPick = action === 'already_represented';
    const canApply = Boolean(action) && (!needsChartPick || Boolean(selectedChartMedications[item.id]));
    const itemLabel = `${medicationLabel(item)}${labelSuffix}`;
    return (
      <div className="mt-3 flex flex-col gap-2">
        <label className="block text-sm">
          <span className="mb-1 block font-medium text-slate-700 dark:text-slate-300">Mark as…</span>
          <select
            aria-label={`Reconciliation decision for ${itemLabel}`}
            className="min-h-[44px] w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
            value={action}
            disabled={isReconciling}
            onChange={(event) => setSelectedAction((current) => ({
              ...current,
              [item.id]: event.target.value as Disposition | '',
            }))}
          >
            <option value="">Choose an action…</option>
            {MARK_AS_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
        {needsChartPick && (
          <>
            <select
              aria-label={`Existing chart medication for ${itemLabel}`}
              className="min-h-[44px] w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
              value={selectedChartMedications[item.id] || ''}
              disabled={isReconciling}
              onChange={(event) => setSelectedChartMedications((current) => ({
                ...current,
                [item.id]: event.target.value,
              }))}
            >
              <option value="">Select the matching chart medication</option>
              {chartMedications.map((medication) => (
                <option key={medication.id} value={medication.id}>
                  {chartMedicationOptionLabel(medication)}
                </option>
              ))}
            </select>
            <label className="flex min-h-[44px] items-center gap-2 text-sm text-slate-700 dark:text-slate-300">
              <input
                type="checkbox"
                aria-label={`Update chart medication from payer details for ${itemLabel}`}
                className="h-4 w-4"
                checked={Boolean(updateFromHistory[item.id])}
                disabled={!selectedChartMedications[item.id] || isReconciling}
                onChange={(event) => setUpdateFromHistory((current) => ({
                  ...current,
                  [item.id]: event.target.checked,
                }))}
              />
              Also update the chart medication with the payer&apos;s details (name, strength, route, directions)
            </label>
          </>
        )}
        <button
          type="button"
          className="min-h-[44px] w-full rounded-md border border-green-600 bg-green-600 px-3 py-2 text-left text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50 dark:border-green-700 dark:bg-green-700 dark:hover:bg-green-600"
          onClick={() => action && void reconcile(item, action)}
          disabled={!canApply || isReconciling}
        >
          {isReconciling ? 'Saving…' : 'Save decision'}
        </button>
      </div>
    );
  };

  return (
    <section className="mt-4 rounded-lg border border-slate-300 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-900">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h3 className="text-base font-semibold">Insurance medication history</h3>
          <p className="mt-1 text-sm text-slate-700 dark:text-slate-300">
            History found through insurance is evidence for review, not proof the patient is currently taking it.
          </p>
        </div>
        <button
          type="button"
          className="min-h-[44px] rounded-md border border-slate-500 bg-white px-3 py-2 text-sm font-medium hover:bg-slate-100"
          onClick={() => setExpanded((value) => !value)}
        >
          {expanded ? 'Hide history review' : 'Review insurance history'}
        </button>
      </div>
      {expanded && (
        <div className="mt-4 space-y-4">
          <button
            type="button"
            className="min-h-[44px] rounded-md bg-slate-800 px-3 py-2 text-sm font-medium text-white hover:bg-slate-700 disabled:opacity-50"
            onClick={() => void syncHistory()}
            disabled={loading}
          >
            {loading ? 'Importing history…' : 'Import latest insurance history'}
          </button>
          <div role="tablist" aria-label="Medication history review status" className="flex flex-wrap gap-2">
            <button
              type="button"
              role="tab"
              aria-selected={!showReviewed}
              className={`min-h-[44px] rounded-md border px-3 py-2 text-sm font-medium ${
                !showReviewed
                  ? 'border-slate-800 bg-slate-800 text-white'
                  : 'border-slate-500 bg-white hover:bg-slate-100'
              }`}
              onClick={() => setShowReviewed(false)}
            >
              Needs Review
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={showReviewed}
              className={`min-h-[44px] rounded-md border px-3 py-2 text-sm font-medium ${
                showReviewed
                  ? 'border-slate-800 bg-slate-800 text-white'
                  : 'border-slate-500 bg-white hover:bg-slate-100'
              }`}
              onClick={() => setShowReviewed(true)}
            >
              Already Reviewed
            </button>
          </div>
          {groups.map(({ latest: item, earlier }) => {
            const renderReviewedSummary = (entry: HistoryItem) => {
              const reviewedLabel = entry.disposition === 'historical_not_current'
                ? 'Historical'
                : dispositionLabels[entry.disposition as Disposition] || entry.disposition;
              return (
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <span className="ml-2 text-sm font-medium text-slate-600 dark:text-slate-300">
                      Reviewed: {reviewedLabel}
                    </span>
                  </div>
                  <button
                    type="button"
                    className="min-h-[44px] rounded-md border border-slate-500 px-3 py-2 text-sm font-medium hover:bg-slate-100"
                    onClick={() => setEditingReviewed((current) => ({
                      ...current,
                      [entry.id]: true,
                    }))}
                  >
                    Change decision
                  </button>
                </div>
              );
            };
            const renderEntryBody = (entry: HistoryItem, labelSuffix = '') => (
              <>
                <div className="mt-1 text-sm text-slate-700 dark:text-slate-300">
                  {observationDetail(entry)}
                </div>
                {entry.reviewed && !editingReviewed[entry.id]
                  ? renderReviewedSummary(entry)
                  : (
                    <>
                      {renderActionControls(entry, labelSuffix)}
                      <div className="mt-2 text-sm text-slate-600 dark:text-slate-400">
                        {entry.reviewed
                          ? `Reviewed: ${dispositionLabels[entry.disposition as Disposition] || entry.disposition}`
                          : 'Needs review'}
                      </div>
                    </>
                  )}
              </>
            );
            return (
              <div key={item.id} className="rounded-md border border-slate-300 bg-white p-3 dark:border-slate-700 dark:bg-slate-950">
                <div className="font-medium">{medicationLabel(item)}</div>
                {renderEntryBody(item)}
                {earlier.length > 0 && (
                  <div className="mt-2">
                    <button
                      type="button"
                      className="text-sm font-medium text-indigo-700 hover:text-indigo-900"
                      aria-expanded={Boolean(expandedGroups[item.id])}
                      onClick={() => setExpandedGroups((current) => ({
                        ...current,
                        [item.id]: !current[item.id],
                      }))}
                    >
                      {expandedGroups[item.id]
                        ? 'Hide earlier fill observations'
                        : `Show ${earlier.length} earlier fill observation${earlier.length === 1 ? '' : 's'}`}
                    </button>
                    {expandedGroups[item.id] && (
                      <ul className="mt-2 space-y-3 border-l-2 border-slate-200 pl-3 text-sm text-slate-600 dark:border-slate-700 dark:text-slate-400">
                        {earlier.map((fill) => (
                          <li key={fill.id}>
                            {renderEntryBody(fill, ` — earlier fill ${fill.last_fill_date || 'unknown date'}`)}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </div>
            );
          })}
          {message && <p className="text-sm text-slate-700 dark:text-slate-300" role="status">{message}</p>}
          {!loading && items.length === 0 && (
            <p className="text-sm text-slate-700 dark:text-slate-300">
              {showReviewed ? 'No reviewed insurance history is available.' : 'No insurance history is waiting for review.'}
            </p>
          )}
        </div>
      )}
    </section>
  );
}
